import { TaskStatus } from "@repo/api/client";
import {
	Button,
	ColorName,
	Flex,
	Icon,
	IconName,
	Menu,
	MenuItem,
	MenuTrigger,
	Popover,
	SubmenuTrigger,
	Text,
} from "@repo/ui";
import { formatTimestampRelative } from "@repo/utils/dateUtils";
type StatusPickerProps = {
	status: TaskStatus;
	unsnoozeDate: Date | undefined;
	onChange: (status: TaskStatus, unsnoozeDate: Date | undefined) => void;
};

type StatusOption = {
	value: TaskStatus;
	label: string;
	background: ColorName;
	color: ColorName;
	icon: IconName;
};

const statusOptions: Record<TaskStatus, StatusOption> = {
	[TaskStatus.ActionRequired]: {
		value: TaskStatus.ActionRequired,
		label: "Action Required",
		background: "red3",
		color: "red10",
		icon: "AlertCircle",
	},
	[TaskStatus.Completed]: {
		value: TaskStatus.Completed,
		label: "Completed",
		background: "green3",
		color: "green10",
		icon: "Check",
	},
	[TaskStatus.Snoozed]: {
		value: TaskStatus.Snoozed,
		label: "Snoozed",
		background: "blue3",
		color: "blue10",
		icon: "Clock",
	},
};

export type SnoozeOption = {
	value: string;
	label: string;
	days: number | null; // null means indefinite (until new message)
	icon:
		| "Clock"
		| "Calendar"
		| "CalendarDays"
		| "CalendarRange"
		| "BotMessageSquare";
};

export const snoozeOptions: SnoozeOption[] = [
	{
		value: "1_day",
		label: "1 day",
		days: 1,
		icon: "Clock",
	},
	{
		value: "3_days",
		label: "3 days",
		days: 3,
		icon: "Calendar",
	},
	{
		value: "1_week",
		label: "1 week",
		days: 7,
		icon: "CalendarDays",
	},
	{
		value: "1_month",
		label: "1 month",
		days: 30,
		icon: "CalendarRange",
	},
	{
		value: "until_new_activity",
		label: "Until new activity",
		days: null, // indefinite
		icon: "BotMessageSquare",
	},
];

export function calculateSnoozeDate(days: number | null): Date | undefined {
	if (days === null) {
		// "Until new activity" - indefinite snooze
		return undefined;
	} else if (days === -1) {
		// "Unsnooze" - return undefined to clear snooze
		return undefined;
	} else {
		// Time-based snooze
		const snoozeDate = new Date();
		snoozeDate.setDate(snoozeDate.getDate() + days);
		return snoozeDate;
	}
}

export function StatusPicker({
	status,
	unsnoozeDate,
	onChange,
}: StatusPickerProps) {
	const currentStatus = statusOptions[status];

	const handleSnoozeSelection = (option: SnoozeOption | "unsnooze") => {
		if (option === "unsnooze") {
			onChange(TaskStatus.ActionRequired, undefined);
			return;
		} else {
			const snoozeDate = calculateSnoozeDate(option.days);
			onChange(TaskStatus.Snoozed, snoozeDate);
		}
	};

	const label =
		status !== TaskStatus.Snoozed || !unsnoozeDate
			? currentStatus.label
			: `Snoozed for ${formatTimestampRelative(unsnoozeDate)}`;

	return (
		<MenuTrigger>
			<Button
				size="sm"
				variant="ghost"
				icon={currentStatus.icon}
				iconColor={currentStatus.color}
			>
				{label}
			</Button>
			<Popover placement="bottom end">
				<Menu>
					{/* Regular status options */}
					<MenuItem
						onAction={() => onChange(TaskStatus.ActionRequired, undefined)}
					>
						<Flex gap="xs" flex align="center">
							<Icon name="AlertCircle" color="red10" size="sm" />
							<Text size="sm" weight="medium" style={{ userSelect: "none" }}>
								Action Required
							</Text>
						</Flex>
						{status === TaskStatus.ActionRequired && (
							<Icon name="Check" color="accent9" size="sm" />
						)}
					</MenuItem>

					{/* Snoozed submenu */}
					<SubmenuTrigger>
						<MenuItem>
							<Flex gap="xs" flex align="center">
								<Icon
									name={statusOptions[TaskStatus.Snoozed].icon}
									color={statusOptions[TaskStatus.Snoozed].color}
									size="sm"
								/>
								<Text size="sm" weight="medium" style={{ userSelect: "none" }}>
									{statusOptions[TaskStatus.Snoozed].label}
								</Text>
							</Flex>
							{status === TaskStatus.Snoozed && (
								<Icon name="Check" color="accent9" size="sm" />
							)}
							<Icon name="ChevronRight" color="sand11" size="sm" />
						</MenuItem>
						<Popover>
							<Menu>
								{status === TaskStatus.Snoozed && unsnoozeDate && (
									<MenuItem
										key="unsnooze"
										onAction={() => handleSnoozeSelection("unsnooze")}
									>
										<Flex gap="xs" align="center">
											<Icon name="Clock" size="sm" />
											<Text
												size="sm"
												weight="medium"
												style={{ userSelect: "none" }}
											>
												Unsnooze
											</Text>
										</Flex>
									</MenuItem>
								)}
								{snoozeOptions.map((option) => (
									<MenuItem
										key={option.value}
										onAction={() => handleSnoozeSelection(option)}
									>
										<Flex gap="xs" align="center">
											<Icon name={option.icon} size="sm" />
											<Text
												size="sm"
												weight="medium"
												style={{ userSelect: "none" }}
											>
												{option.label}
											</Text>
										</Flex>
									</MenuItem>
								))}
							</Menu>
						</Popover>
					</SubmenuTrigger>

					<MenuItem onAction={() => onChange(TaskStatus.Completed, undefined)}>
						<Flex gap="xs" flex align="center">
							<Icon name="Check" color="green10" size="sm" />
							<Text size="sm" weight="medium" style={{ userSelect: "none" }}>
								Completed
							</Text>
						</Flex>
						{status === TaskStatus.Completed && (
							<Icon name="Check" color="accent9" size="sm" />
						)}
					</MenuItem>
				</Menu>
			</Popover>
		</MenuTrigger>
	);
}
